import { C_PROPERTIES_STATUS } from "@/src-refactor/modules/shared/model/const/property/real-estate-status";
import useUserHelp from "@rf-hooks/useUserHelp";

interface IAgent {
  id: number;
  agencyId?: number;
}

export interface IUsePermissionProperty {
  idStatusProperty: number;
  isProject: boolean;
  agents: IAgent[];
  hasImages?: boolean;
}

/**
 * Obtiene los estados booleanos de la propiedad basados en su ID de estado
 */
const getPropertyStatus = (idStatus: number) => {
  return {
    active: idStatus === C_PROPERTIES_STATUS.ACTIVE.id,
    negotiation: idStatus === C_PROPERTIES_STATUS.INNEGOTIATION.id,
    inConflict: idStatus === C_PROPERTIES_STATUS.INCONFLICT.id,
    dropped: idStatus === C_PROPERTIES_STATUS.DROPPED.id,
    pending: idStatus === C_PROPERTIES_STATUS.PENDING.id
  };
};

/**
 * Verifica si el usuario es un agente captador de la propiedad
 */
const isRecruitingAgent = (agents: IAgent[], userId: number) => {
  return agents.some(agent => agent.id === userId);
};

/**
 * Verifica si el admin pertenece a la agencia de alguno de los agentes captadores
 */
const checkAdminOfAgency = (agents: IAgent[], userAgencyId: number) => {
  return agents.some(agent => agent.agencyId === userAgencyId);
};

/**
 * Hook para gestionar permisos de propiedades
 *
 * Centraliza toda la lógica de permisos basada en:
 * - Rol del usuario (Super Admin, Admin, Agente)
 * - Estado de la propiedad (Activa, Pendiente, Baja, etc.)
 * - Relación del usuario con la propiedad (Captador, Agencia)
 *
 * @returns {Object} Objeto con función getPermissions
 */
const usePermissionProperty = () => {
  const { USER, USER_ROLE } = useUserHelp();

  /**
   * Calcula todos los permisos para una propiedad específica
   *
   * @param {IUsePermissionProperty} arg - Datos de la propiedad y agentes
   * @returns {Object} Objeto con todos los permisos booleanos
   */
  const getPermissions = (arg: IUsePermissionProperty) => {
    const { idStatusProperty, isProject, agents, hasImages = false } = arg;

    const status = getPropertyStatus(idStatusProperty);

    const isRecruiting = USER?.userData?.people_id
      ? isRecruitingAgent(agents, USER.userData.people_id)
      : false;

    const isAdminOfAgency = USER?.userData?.agency?.id
      ? checkAdminOfAgency(agents, USER.userData.agency.id)
      : false;

    const userLoggedIsCreator = isRecruiting;

    return {
      // ============ PERMISOS BÁSICOS ============

      /** Permiso para ver la propiedad */
      canViewProperty: true,

      /** Permiso para crear propiedades */
      canCreateProperty: true,

      /** Permiso para listar propiedades */
      canListProperty: true,

      // ============ PERMISOS DE EDICIÓN ============

      /**
       * Permiso para editar/actualizar la propiedad
       * - Super Admin: Siempre
       * - Admin: Si pertenece a la agencia de algún captador
       * - Agente: Si es captador de la propiedad
       */
      canUpdateProperty: USER_ROLE.SUPER_ADMIN || isAdminOfAgency || isRecruiting,

      /**
       * Permiso para eliminar la propiedad
       * Solo Super Admin
       */
      canDeleteProperty: USER_ROLE.SUPER_ADMIN,

      // ============ PERMISOS DE COMPARTIR ============

      /**
       * Permiso para referir la propiedad a otros agentes
       * Solo si la propiedad está ACTIVA
       */
      canReferProperty: status.active,

      /** Alias de canReferProperty */
      canShareProperty: status.active,

      // ============ PERMISOS DE REPORTES ============

      /**
       * Permiso para reportar comisión
       * - Solo si está ACTIVA o EN NEGOCIACIÓN
       * - No aplica para proyectos
       */
      canReportCommission: (status.negotiation || status.active) && !isProject,

      /**
       * Permiso para reportar problemas con la propiedad
       * Siempre disponible
       */
      canReportProperty: true,

      // ============ PERMISOS DE ACTIVACIÓN ============

      /**
       * Permiso para aprobar una propiedad PENDIENTE
       * Solo Super Admin
       */
      canActivateProperty: USER_ROLE.SUPER_ADMIN && status.pending,

      /**
       * Permiso para extender disponibilidad de propiedad ACTIVA
       * Solo Super Admin
       */
      canExtendTime: USER_ROLE.SUPER_ADMIN && status.active,

      /**
       * Permiso para activar y extender propiedades DADAS DE BAJA o EN NEGOCIACIÓN
       * Solo Super Admin
       */
      canExtendTimeAndActivate:
        USER_ROLE.SUPER_ADMIN && (status.dropped || status.negotiation),

      /**
       * Permiso para reactivar propiedad (legacy)
       * - Super Admin o Admin de la agencia
       * - Solo si está DADA DE BAJA o EN NEGOCIACIÓN
       */
      canReactivateProperty:
        (USER_ROLE.SUPER_ADMIN || isAdminOfAgency) &&
        (status.dropped || status.negotiation),

      // ============ PERMISOS DE DAR DE BAJA ============

      /**
       * Permiso para dar de baja una propiedad
       * - Super Admin: Cualquier propiedad excepto las ya dadas de baja
       * - Agente Captador: Solo sus propiedades en estados distinto a DADA DE BAJA
       */
      canDropProperty:
        (USER_ROLE.SUPER_ADMIN || userLoggedIsCreator) && !status.dropped,
      // ============ PERMISOS DE SOLICITUDES ============

      /**
       * Permiso para solicitar reactivación
       * - Solo agentes captadores
       * - Solo si está DADA DE BAJA o EN NEGOCIACIÓN
       *    canRequestReactivation:
       */
      canRequestReactivation: (status.dropped || status.negotiation) && isRecruiting,

      // ============ PERMISOS DE CONFLICTOS ============

      /**
       * Permiso para resolver conflictos
       * - Solo Super Admin
       * - Solo si la propiedad está EN CONFLICTO
       */
      canResolveConflict: status.inConflict && USER_ROLE.SUPER_ADMIN,

      // ============ PERMISOS DE VISUALIZACIÓN WEB ============

      /**
       * Permiso para ver la propiedad en el sitio web público
       * Solo si está ACTIVA
       */
      canViewOnWebsite: status.active,

      // ============ PERMISOS DE DESCARGA ============

      /**
       * Permiso para descargar PDF/brochure de la propiedad
       * Siempre disponible
       */
      canDownloadProperty: true,

      /** Alias de canDownloadProperty */
      canDownloadPDF: true,

      /**
       * Permiso para descargar imágenes de la propiedad
       * Solo si la propiedad tiene imágenes
       */
      canDownloadImages: hasImages,

      // ============ PERMISOS DE LOG ============

      /**
       * Permiso para ver el log de datos
       * Siempre disponible
       */
      canLogData: true
    };
  };

  return {
    getPermissions
  };
};

export default usePermissionProperty;
